import { useState, useEffect } from 'react';
import { Sparkles, ArrowRight, Code, Trello, PhoneCall, Mic, Play, CheckCircle, Calendar, TrendingUp, Target, Zap } from 'lucide-react';

export default function EnhancedHero() {
  const [currentRole, setCurrentRole] = useState<'code' | 'product' | 'sales'>('code');

  const roles = {
    code: {
      title: 'Engineering Assessment',
      desc: 'Live coding environment with AI proctoring',
      persona: 'Paruksha DBB',
      icon: Code,
      color: 'emerald'
    },
    product: {
      title: 'Product Strategy Round',
      desc: 'AI-powered roadmap planning with dependency mapping',
      persona: 'Sandesh L.',
      icon: Trello,
      color: 'teal'
    },
    sales: {
      title: 'Sales Pitch Simulation',
      desc: 'Live pitch scenarios with AI buyer & objection tracking',
      persona: 'Aashirwad M.',
      icon: PhoneCall,
      color: 'cyan'
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentRole(prev => {
        if (prev === 'code') return 'product';
        if (prev === 'product') return 'sales';
        return 'code';
      });
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const currentRoleData = roles[currentRole];

  const renderToolContent = () => {
    switch (currentRole) {
      case 'code':
        return (
          <div className="bg-slate-900 rounded-xl border border-emerald-500/30 overflow-hidden h-48">
            <div className="bg-slate-800 px-4 py-2 flex items-center gap-2 border-b border-white/10">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
              <span className="text-xs text-slate-400 font-mono ml-2">solution.py</span>
            </div>
            <div className="p-4 font-mono text-xs leading-relaxed text-slate-300">
              <span className="text-purple-400">def</span> <span className="text-yellow-400">sort_users</span>(data):<br />
              &nbsp;&nbsp;<span className="text-slate-400"># Sort by engagement score</span><br />
              &nbsp;&nbsp;<span className="text-purple-400">return</span> <span className="text-blue-400">sorted</span>(data, <br />
              &nbsp;&nbsp;&nbsp;&nbsp;key=<span className="text-purple-400">lambda</span> x: x[<span className="text-green-400">'score'</span>])<br />
              <br />
              <span className="text-emerald-400">● AI: Analyzing complexity...</span>
            </div>
            <div className="absolute bottom-3 right-3 px-3 py-1.5 bg-emerald-500/20 border border-emerald-500 rounded-lg text-[10px] text-emerald-400 font-bold flex items-center gap-2">
              <CheckCircle className="w-3 h-3" />
              SYNTAX VALID
            </div>
          </div>
        );
      case 'product':
        return (
          <div className="bg-slate-900 rounded-xl border border-teal-500/30 overflow-hidden h-48 relative">
            <div className="bg-slate-800 px-3 py-2 flex items-center justify-between border-b border-white/10">
              <div className="flex items-center gap-2">
                <Calendar className="w-3.5 h-3.5 text-teal-400" />
                <span className="text-[10px] text-slate-400 font-semibold">Roadmap Planner 2024</span>
              </div>
              <div className="text-[8px] text-teal-400 font-bold">Q1-Q4</div>
            </div>
            <div className="p-3 space-y-2.5">
              <div className="grid grid-cols-4 gap-1.5">
                <div className="bg-slate-800/50 rounded px-2 py-1.5 border-l-2 border-blue-500">
                  <div className="text-[7px] text-blue-400 font-bold mb-0.5">Q1</div>
                  <div className="text-[8px] text-slate-300 leading-tight">Auth System</div>
                  <div className="flex items-center gap-1 mt-1">
                    <Target className="w-2 h-2 text-green-400" />
                    <span className="text-[6px] text-green-400">HIGH</span>
                  </div>
                </div>
                <div className="bg-slate-800/50 rounded px-2 py-1.5 border-l-2 border-teal-500">
                  <div className="text-[7px] text-teal-400 font-bold mb-0.5">Q2</div>
                  <div className="text-[8px] text-slate-300 leading-tight">AI Chat</div>
                  <div className="flex items-center gap-1 mt-1">
                    <Zap className="w-2 h-2 text-yellow-400" />
                    <span className="text-[6px] text-yellow-400">MED</span>
                  </div>
                </div>
                <div className="bg-slate-800/50 rounded px-2 py-1.5 border-l-2 border-purple-500">
                  <div className="text-[7px] text-purple-400 font-bold mb-0.5">Q3</div>
                  <div className="text-[8px] text-slate-300 leading-tight">Analytics</div>
                  <div className="flex items-center gap-1 mt-1">
                    <TrendingUp className="w-2 h-2 text-blue-400" />
                    <span className="text-[6px] text-blue-400">MED</span>
                  </div>
                </div>
                <div className="bg-slate-800/50 rounded px-2 py-1.5 border-l-2 border-orange-500">
                  <div className="text-[7px] text-orange-400 font-bold mb-0.5">Q4</div>
                  <div className="text-[8px] text-slate-300 leading-tight">Mobile App</div>
                  <div className="flex items-center gap-1 mt-1">
                    <Target className="w-2 h-2 text-orange-400" />
                    <span className="text-[6px] text-orange-400">LOW</span>
                  </div>
                </div>
              </div>
              <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-2">
                <div className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-teal-400 mt-1"></div>
                  <div>
                    <div className="text-[9px] text-teal-300 font-bold mb-0.5">AI Prioritizing:</div>
                    <div className="text-[8px] text-slate-300 leading-relaxed">
                      "Q1 Auth blocks Q2 Chat. Recommend advancing timeline +2 weeks"
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute bottom-2 right-2 px-2.5 py-1 bg-teal-500/90 border border-teal-600 rounded-lg text-[9px] text-white font-bold flex items-center gap-1.5">
              <Play className="w-2.5 h-2.5" />
              DEPENDENCIES MAPPED
            </div>
          </div>
        );
      case 'sales':
        return (
          <div className="bg-slate-900 rounded-xl border border-cyan-500/30 overflow-hidden h-48 relative flex flex-col">
            <div className="bg-slate-800 px-3 py-2 flex items-center justify-between border-b border-white/10">
              <div className="flex items-center gap-2">
                <PhoneCall className="w-3.5 h-3.5 text-cyan-400" />
                <span className="text-[10px] text-slate-400 font-semibold">Live Pitch Simulator</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></div>
                <span className="text-[8px] text-red-400 font-bold">RECORDING</span>
              </div>
            </div>
            <div className="flex-1 p-2.5 space-y-2 overflow-hidden">
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-2">
                <div className="flex items-start gap-2">
                  <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center text-white text-[8px] font-bold flex-shrink-0">AI</div>
                  <div className="flex-1">
                    <div className="text-[8px] text-orange-300 font-bold mb-0.5">Enterprise Buyer - Skeptical Mode</div>
                    <div className="text-[9px] text-slate-300 leading-relaxed">
                      "We're locked into competitor X. Migration costs are too high."
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2">
                <div className="flex items-start gap-2">
                  <div className="w-6 h-6 rounded-full bg-cyan-500 flex items-center justify-center text-white text-[8px] font-bold flex-shrink-0">C</div>
                  <div className="flex-1">
                    <div className="text-[8px] text-cyan-300 font-bold mb-0.5">Candidate Response</div>
                    <div className="text-[9px] text-white leading-relaxed">
                      "I understand. Let me show you our white-glove migration service..."
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex gap-1.5 items-center px-2">
                <div className="flex-1 h-1 bg-slate-800 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500" style={{ width: '65%' }}></div>
                </div>
                <span className="text-[8px] text-cyan-400 font-bold">65%</span>
              </div>
            </div>
            <div className="bg-slate-800/50 px-2.5 py-2 flex items-center justify-between border-t border-white/10">
              <div className="flex gap-3">
                <div className="text-[8px]">
                  <span className="text-slate-400">Objections:</span>
                  <span className="text-green-400 font-bold ml-1">3/5</span>
                </div>
                <div className="text-[8px]">
                  <span className="text-slate-400">Tone:</span>
                  <span className="text-cyan-400 font-bold ml-1">CONFIDENT</span>
                </div>
              </div>
              <div className="px-2 py-0.5 bg-cyan-500/90 border border-cyan-600 rounded text-[8px] text-white font-bold">
                LIVE ANALYSIS
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">

      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-96 h-96 bg-emerald-500 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-teal-500 rounded-full filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="container mx-auto px-6 pt-32 pb-24 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 glass rounded-full">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-medium text-white">Excellence Meets Approval</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold leading-tight text-white italic">
              Hire the Right Person. <br />
              <span className="bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent not-italic">
                In 48 Hours.
              </span>
            </h1>

            <p className="text-xl text-slate-300 max-w-lg leading-relaxed">
              GreenTick AI handles deep interaction with candidates to capture behavioral signals while simultaneously assessing proficiency via integrated tools.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://calendar.app.google/SxKx5dUS1G1pBYTFA"
                target="_blank"
                rel="noopener noreferrer"
                className="group px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-semibold hover:shadow-2xl hover:shadow-emerald-500/50 transition-all hover:scale-105 flex items-center gap-2 justify-center"
              >
                Start Pilot
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="https://dashboard.greentick.org.in/demo"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl font-semibold hover:from-teal-600 hover:to-emerald-600 transition-all hover:scale-105 flex items-center justify-center shadow-lg shadow-teal-500/30"
              >
                Try Demo
              </a>
            </div>

            <div className="mt-12 pt-8 border-t border-white/10">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">AI Controlling Live Tools</p>
                <div className="flex bg-black/40 rounded-lg p-1 border border-white/10 backdrop-blur-md gap-2">
                  {(['code', 'product', 'sales'] as const).map((role) => {
                    const RoleIcon = roles[role].icon;
                    const roleLabel = role === 'code' ? 'Engineering' : role === 'product' ? 'Product Strategy' : 'Sales Simulation';
                    return (
                      <button
                        key={role}
                        onClick={() => setCurrentRole(role)}
                        aria-label={`Switch to ${roleLabel} view`}
                        className={`px-3 py-2 rounded-md text-xs font-medium transition-all flex items-center gap-2 ${
                          currentRole === role
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                            : 'text-slate-400 hover:text-white'
                        }`}
                      >
                        <RoleIcon className="w-3 h-3" aria-hidden="true" />
                        <span className="hidden sm:inline">{role === 'code' ? 'Eng' : role === 'product' ? 'Product' : 'Sales'}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <p className="text-xs text-slate-400 font-mono">{currentRoleData.desc}</p>
            </div>
          </div>

          <div className="flex items-center justify-center">
            <div className="relative bg-slate-900 rounded-3xl p-8 shadow-2xl transform transition-transform duration-500 border border-emerald-500/30 w-full max-w-lg">
              <div className="flex justify-between items-center mb-8">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                      {currentRoleData.persona.charAt(0)}
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-400 border-2 border-slate-900 rounded-full animate-pulse"></div>
                  </div>
                  <div>
                    <h2 className="font-bold text-lg text-white">{currentRoleData.persona}</h2>
                    <div className="flex items-center gap-2 text-xs text-slate-400 font-mono mt-1">
                      <span className="text-emerald-400">● LIVE</span>
                      <span>|</span>
                      <span>{currentRoleData.title}</span>
                    </div>
                  </div>
                </div>
                 <div 
                  className="h-10 w-10 rounded-full bg-emerald-500/20 flex items-center justify-center border border-emerald-500/30 animate-pulse"
                  aria-label="Microphone active"
                  role="img"
                >
                  <Mic className="w-5 h-5 text-emerald-400" aria-hidden="true" />
                </div>
              </div>

              <div className="mb-6">
                <div className="flex justify-between items-end mb-2 text-[10px] text-slate-400 font-mono uppercase">
                  <span>Voice Stream</span>
                  <span>-24dB</span>
                </div>
                <div className="h-16 bg-black/50 rounded-xl border border-emerald-500/30 flex items-center justify-center gap-1.5 px-4">
                  {[...Array(6)].map((_, i) => (
                    <div
                      key={i}
                      className="w-1.5 bg-emerald-400 rounded-full wave-bar"
                      style={{
                        animationDelay: `${i * 0.1}s`,
                        height: `${20 + Math.random() * 40}px`
                      }}
                    ></div>
                  ))}
                </div>
              </div>

              <div className="relative">
                {renderToolContent()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
